import React, { useState, useCallback, useEffect, useRef } from 'react';
import { X, Heart, Share2, ChevronRight, Phone } from 'lucide-react';
import cr3ateWordmark from '../assets/cr3ate-ai-logo.svg';
import { MEDICAL_LAKE_LISTING } from '../config/medicalLakeListing';

type FullscreenTab = 'photos' | '3d' | 'floorplans' | 'map';

const PHOTOS = [
  '/medical-lake-exterior.jpg',
  '__3d_exterior__',
  '/medical-lake-living.jpg',
  '/medical-lake-kitchen.jpg',
  '/medical-lake-bedroom.jpg',
  '/medical-lake-bedroom2.jpg',
  '/medical-lake-bathroom.jpg',
  '/medical-lake-backyard.jpg',
  '/medical-lake-shed.jpg',
];

const PHOTO_CATEGORIES = [
  { key: 'all', label: 'All photos', count: 30 },
  { key: 'kitchen', label: 'Kitchen' },
  { key: 'bathroom', label: 'Bathroom' },
  { key: 'bedroom', label: 'Bedroom' },
  { key: 'living', label: 'Living' },
  { key: 'dining', label: 'Dining' },
];

const TOUR_TIMES = ['12:00 pm', '1:00 pm', '2:00 pm', '3:00 pm', '4:00 pm'];

const PROPERTY_DETAILS = [
  { label: 'Property Type', value: 'Single-family' },
  { label: 'Year Built', value: '2004' },
  { label: 'Lot Size', value: `${MEDICAL_LAKE_LISTING.lotAreaSqFt.toLocaleString('en-US')} sq ft` },
  { label: 'Price/Sq.Ft.', value: '$220' },
  { label: 'Parking', value: '2 car garage' },
  { label: 'MLS #', value: '202625460' },
];

export default function MedicalLakeRedfinDemo() {
  const [fullscreenTab, setFullscreenTab] = useState<FullscreenTab | null>(null);
  const [photoCategory, setPhotoCategory] = useState('all');
  const [saved, setSaved] = useState(false);
  const tileViewerRef = useRef<HTMLIFrameElement>(null);
  const galleryViewerRef = useRef<HTMLIFrameElement>(null);
  useEffect(() => {
    const onExplore = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.data?.type !== 'cr3ate:explore-realestate') return;
      if (event.source !== tileViewerRef.current?.contentWindow && event.source !== galleryViewerRef.current?.contentWindow) return;
      setFullscreenTab('3d');
    };
    window.addEventListener('message', onExplore);
    return () => window.removeEventListener('message', onExplore);
  }, []);


  const openTab = useCallback((tab: FullscreenTab) => setFullscreenTab(tab), []);
  const closeFullscreen = useCallback(() => setFullscreenTab(null), []);

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900 antialiased">

      {/* ── Redfin sticky header ──────────────────────────────────────── */}
      <header className="sticky top-0 z-40 bg-[#CC0000] shadow">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2">
              <svg width="22" height="22" viewBox="0 0 40 40" fill="none">
                <circle cx="20" cy="20" r="20" fill="white" fillOpacity="0.18" />
                <path
                  d="M20 8C13.4 8 8 13.4 8 20s5.4 12 12 12 12-5.4 12-12S26.6 8 20 8zm0 4c1.7 0 3.2.6 4.4 1.6L12.6 24.4C11.6 23.2 11 21.7 11 20c0-5 4-9 9-9zm0 18c-1.7 0-3.2-.6-4.4-1.6l11.8-11.8c1 1.2 1.6 2.7 1.6 4.4 0 5-4 9-9 9z"
                  fill="white"
                />
              </svg>
              <span className="text-white font-bold text-xl tracking-tight">redfin</span>
            </div>
            <nav className="hidden md:flex items-center gap-6 text-white/90 text-sm font-medium">
              {['Buy', 'Sell', 'Rent', 'Mortgage'].map((item) => (
                <a key={item} href="#" className="hover:text-white transition-colors">{item}</a>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <button className="text-white/80 hover:text-white text-sm transition-colors">Log In</button>
            <button className="bg-white text-[#CC0000] font-semibold text-sm px-4 py-1.5 rounded-full hover:bg-red-50 transition-colors">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* ── Sub-nav ────────────────────────────────────────────────────── */}
      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 h-11 flex items-center justify-between text-sm">
          <div className="flex items-center gap-1 text-gray-500">
            <button className="text-[#CC0000] hover:underline font-medium">Search</button>
            <ChevronRight size={13} className="text-gray-300" />
            <button className="font-semibold text-gray-900">Overview</button>
            {['Neighborhood', 'Property details', 'Sale & tax history', 'Climate'].map((item) => (
              <React.Fragment key={item}>
                <span className="text-gray-200 mx-2">|</span>
                <button className="hover:text-gray-700 transition-colors">{item}</button>
              </React.Fragment>
            ))}
          </div>
          <div className="flex items-center gap-4 text-gray-500">
            <button className="flex items-center gap-1.5 hover:text-gray-800 transition-colors">
              <Heart size={14} />
              Favorite
            </button>
            <button className="flex items-center gap-1.5 hover:text-gray-800 transition-colors">
              <Share2 size={14} />
              Share
            </button>
          </div>
        </div>
      </div>

      {/* ── Photo grid ────────────────────────────────────────────────── */}
      <div className="relative w-full bg-white p-[3px] rounded-xl overflow-hidden">
        <div className="flex h-[390px] gap-[3px] bg-white">

          {/* Hero photo — left ~38% */}
          <button
            className="relative overflow-hidden group flex-none rounded-lg"
            style={{ flexBasis: '38%' }}
            onClick={() => openTab('photos')}
          >
            <img
              src={PHOTOS[0]}
              alt="Exterior"
              className="w-full h-full object-cover group-hover:brightness-90 transition-all duration-300"
            />
          </button>

          {/* 3D viewer — center ~31%, approx square */}
          <div className="relative overflow-hidden bg-gray-950 flex-none rounded-lg" style={{ flexBasis: '31%' }}>
            <iframe
              ref={tileViewerRef}
              src="/viewer-embed?scene=medical_lake_neighbourhood"
              title="3D Exterior — 326 E Connie Ray Ave"
              className="w-full h-full border-0"
              allow="fullscreen"
            />
          </div>

          {/* Right column — 2 interior photos stacked */}
          <div className="flex flex-col gap-[3px] bg-white flex-1 min-w-0">
            <button className="flex-1 overflow-hidden group rounded-lg" onClick={() => openTab('photos')}>
              <img src={PHOTOS[4]} alt="Bedroom" className="w-full h-full object-cover group-hover:brightness-90 transition-all duration-300" />
            </button>
            <button className="relative flex-1 overflow-hidden group rounded-lg" onClick={() => openTab('photos')}>
              <img src={PHOTOS[3]} alt="Kitchen" className="w-full h-full object-cover group-hover:brightness-90 transition-all duration-300" />
              {/* "See all photos" pill — bottom-right of last tile */}
              <span className="absolute bottom-3 right-3 bg-white text-gray-800 text-xs font-semibold px-3 py-1.5 rounded-full shadow-md border border-gray-100 pointer-events-none">
                See all 30 photos
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Main content ──────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-10">

          {/* Left column */}
          <div className="flex-1 min-w-0">

            {/* Price + address */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                  <span className="text-sm text-gray-500">For sale</span>
                </div>
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                  $496,500
                  <span className="text-base font-normal text-gray-500 ml-3">Est. $3,153/mo</span>
                </h1>
                <div className="flex items-center gap-2 mt-2.5 text-gray-800">
                  <span className="font-bold text-lg">5 bd</span>
                  <span className="text-gray-300">•</span>
                  <span className="font-bold text-lg">3 ba</span>
                  <span className="text-gray-300">•</span>
                  <span className="font-bold text-lg">{MEDICAL_LAKE_LISTING.homeAreaSqFt.toLocaleString('en-US')} sq ft</span>
                </div>
                <p className="text-gray-500 mt-1.5">326 E Connie Ray Ave, Medical Lake, WA 99022</p>
              </div>
              <div className="flex gap-2 mt-1">
                <button
                  onClick={() => setSaved((s) => !s)}
                  className="p-2.5 rounded-full border border-gray-200 hover:bg-red-50 transition-colors"
                >
                  <Heart size={17} className={saved ? 'fill-[#CC0000] text-[#CC0000]' : 'text-gray-400'} />
                </button>
                <button className="p-2.5 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors">
                  <Share2 size={17} className="text-gray-400" />
                </button>
              </div>
            </div>

            {/* Stats bar — homes.com style */}
            <div className="grid grid-cols-4 border border-gray-200 rounded-xl overflow-hidden mb-8 text-center">
              {[
                ['5', 'Beds'],
                ['3', 'Baths'],
                ['2,260', 'Sq Ft'],
                ['$220', 'Price / Sq Ft'],
              ].map(([val, label], i) => (
                <div
                  key={label}
                  className={`py-4 ${i < 3 ? 'border-r border-gray-200' : ''}`}
                >
                  <p className="font-bold text-xl text-gray-900">{val}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{label}</p>
                </div>
              ))}
            </div>

            {/* Popular callout */}
            <div className="border border-gray-200 rounded-xl p-5 mb-8 flex items-start gap-4">
              <div className="flex-none w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                <svg viewBox="0 0 20 20" className="w-5 h-5 text-amber-500" fill="currentColor">
                  <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900">This home is popular</p>
                <p className="text-sm text-gray-500 mt-0.5">It's in the top 10% of views. Tour it before it's gone!</p>
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <span className="text-gray-400">Today, Oct 14</span>
                  {TOUR_TIMES.map((t) => (
                    <button key={t} className="text-[#CC0000] hover:underline font-medium">
                      {t}
                    </button>
                  ))}
                  <button className="text-[#CC0000] hover:underline font-medium">Check for more</button>
                </div>
              </div>
            </div>

            {/* About */}
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-3">About this home</h2>
              <p className="text-gray-600 leading-relaxed text-[0.9375rem]">
                Beautifully <strong className="text-gray-800">remodeled</strong> 5BR, 3BA home in popular Medical Lake!
                Fresh updates include <strong className="text-gray-800">quartz countertops</strong>,{' '}
                <strong className="text-gray-800">tile upgrades</strong>,{' '}
                <strong className="text-gray-800">new flooring</strong> throughout, and{' '}
                <strong className="text-gray-800">modern light fixtures</strong>. The main level features{' '}
                <strong className="text-gray-800">vaulted living spaces</strong>, a primary suite, two additional
                bedrooms, and a full bath. The daylight walkout lower level offers two more bedrooms, a large rec room,
                and a third full bath. Enjoy the deck, fenced level backyard, and attached 2-car garage.
                Located minutes from Medical Lake and Fairchild AFB with easy access to Spokane.
              </p>
            </div>

            {/* Property details grid */}
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4">Property details</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {PROPERTY_DETAILS.map(({ label, value }) => (
                  <div key={label} className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">{label}</p>
                    <p className="font-semibold text-gray-900 text-sm">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Listing meta */}
            <div className="text-sm text-gray-400 border-t border-gray-100 pt-5 space-y-1">
              <p>
                Listed by <span className="text-gray-700 font-medium">Jessica Side</span>
                {' · '}Realty One Group Eclipse
              </p>
              <p>Listing updated: Oct 14, 2026 at 08:06am</p>
              <p>Redfin checked: 2 minutes ago · Source: SPOKANEMLS as Distributed by MLS Grid #202625460</p>
            </div>
          </div>

          {/* Right column — sticky CTA card */}
          <div className="lg:w-[340px] flex-none">
            <div className="sticky top-20 rounded-2xl border border-gray-200 shadow-xl overflow-hidden">

              {/* Primary CTAs */}
              <div className="p-5 space-y-3">
                <button className="w-full bg-[#CC0000] hover:bg-[#b50000] text-white font-semibold py-3.5 rounded-full text-sm transition-colors">
                  Request showing
                </button>
                <p className="text-center text-xs text-gray-400">Tour for free, no strings attached</p>
                <button className="w-full border-2 border-gray-900 text-gray-900 font-semibold py-3.5 rounded-full text-sm hover:bg-gray-50 transition-colors">
                  Start an offer
                </button>
                <p className="text-center text-xs text-gray-400">
                  Make a winning offer with the help of a local agent
                </p>
              </div>

              {/* Contact */}
              <div className="border-t border-gray-100 px-5 py-4 flex items-center justify-between">
                <button className="text-[#CC0000] hover:underline text-sm font-medium">
                  Ask a question
                </button>
                <a
                  href="tel:5094611414"
                  className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 hover:text-[#CC0000] transition-colors"
                >
                  <Phone size={13} />
                  (509) 461-1414
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Fullscreen overlay ────────────────────────────────────────── */}
      {fullscreenTab && (
        <div className="fixed inset-0 z-50 flex flex-col bg-[#111]">

          {/* Top nav bar */}
          <div className="flex-none flex items-center h-14 px-5 bg-[#1c1c1c] border-b border-white/8">
            <button
              onClick={closeFullscreen}
              className="text-white font-semibold text-sm mr-6 whitespace-nowrap opacity-90 hover:opacity-100 hover:underline transition-opacity"
            >
              326 E Connie Ray Ave
            </button>
            <nav className="flex items-center flex-1 gap-0.5">
              {(
                [
                  { key: 'photos', label: 'Photos' },
                  { key: '3d', label: '3D Exterior' },
                  { key: 'floorplans', label: 'Floorplans' },
                  { key: 'map', label: 'Map' },
                ] as const
              ).map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setFullscreenTab(key)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-sm transition-colors relative ${
                    fullscreenTab === key
                      ? 'text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-white'
                      : 'text-white/45 hover:text-white/75'
                  }`}
                >
                  {label}
                </button>
              ))}
            </nav>
            <div className="flex items-center gap-1 ml-4">
              <button
                onClick={() => setSaved((s) => !s)}
                className="p-2 text-white/45 hover:text-white transition-colors rounded-lg hover:bg-white/8"
              >
                <Heart size={17} className={saved ? 'fill-white text-white' : ''} />
              </button>
              <button className="p-2 text-white/45 hover:text-white transition-colors rounded-lg hover:bg-white/8">
                <Share2 size={17} />
              </button>
              <button
                onClick={closeFullscreen}
                className="p-2 text-white/45 hover:text-white transition-colors rounded-lg hover:bg-white/8"
              >
                <X size={17} />
              </button>
            </div>
          </div>

          {/* Content area */}
          <div className="flex-1 relative overflow-hidden">

            {/* 3D Exterior */}
            {fullscreenTab === '3d' && (
              <>
                <iframe
                  src="/realestate-viewer"
                  title="3D Exterior — 326 E Connie Ray Ave"
                  className="w-full h-full border-0"
                  allow="fullscreen"
                />

              </>
            )}

            {/* Photo gallery */}
            {fullscreenTab === 'photos' && (
              <div className="flex h-full">

                {/* Sidebar */}
                <div className="w-[72px] flex-none flex flex-col items-center pt-6 gap-5 border-r border-white/8 bg-[#1c1c1c]">
                  <button className="flex flex-col items-center gap-1.5">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                      <svg viewBox="0 0 20 20" className="w-4.5 h-4.5 text-gray-900" fill="currentColor">
                        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-[10px] text-white/90 font-medium">Photos</span>
                  </button>
                  <button className="flex flex-col items-center gap-1.5 opacity-40 hover:opacity-70 transition-opacity">
                    <div className="w-10 h-10 rounded-full bg-white/8 flex items-center justify-center">
                      <svg viewBox="0 0 20 20" className="w-4.5 h-4.5 text-white" fill="currentColor">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-[10px] text-white/60">Street view</span>
                  </button>
                </div>

                {/* Gallery content */}
                <div className="flex-1 flex flex-col overflow-hidden">

                  {/* Gallery top bar */}
                  <div className="flex-none flex items-center gap-4 px-6 py-3 bg-[#1c1c1c] border-b border-white/8 text-sm">
                    <span className="font-semibold text-white">$496,500</span>
                    <span className="text-white/40">5 bed · 3 bath · {MEDICAL_LAKE_LISTING.homeAreaSqFt.toLocaleString('en-US')} sq ft</span>
                    <span className="text-white/40">326 E Connie Ray Ave, Medical Lake, WA 99022</span>
                    <div className="ml-auto flex items-center gap-3">
                      <button className="text-white/50 hover:text-white transition-colors">Get prequalified</button>
                      <span className="text-white/25">|</span>
                      <button className="text-white/50 hover:text-white transition-colors">
                        Next available: Today at 12 pm
                      </button>
                      <button className="bg-[#CC0000] hover:bg-[#b50000] text-white font-semibold px-4 py-1.5 rounded-full text-xs transition-colors">
                        Request showing
                      </button>
                    </div>
                  </div>

                  {/* Category filter */}
                  <div className="flex-none flex items-center gap-2 px-6 pt-4 pb-3 overflow-x-auto">
                    {PHOTO_CATEGORIES.map(({ key, label, count }) => (
                      <button
                        key={key}
                        onClick={() => setPhotoCategory(key)}
                        className={`flex-none flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          photoCategory === key
                            ? 'bg-white text-gray-900'
                            : 'bg-white/8 text-white/60 hover:bg-white/15 hover:text-white'
                        }`}
                      >
                        {label}
                        {count && (
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${photoCategory === key ? 'bg-gray-200 text-gray-600' : 'bg-white/15'}`}>
                            {count}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Photo grid */}
                  <div className="flex-1 overflow-y-auto px-6 pb-6">
                    <div className="grid grid-cols-3 gap-1.5">
                      {PHOTOS.map((url, i) =>
                        url === '__3d_exterior__' ? (
                          <div key="3d-tile" className="aspect-[4/3] relative overflow-hidden rounded-lg bg-gray-900">
                            <iframe
                              ref={galleryViewerRef}
                              src="/viewer-embed?scene=medical_lake_neighbourhood"
                              title="3D Exterior"
                              className="w-full h-full border-0"
                              allow="fullscreen"
                            />
                          </div>
                        ) : (
                          <div key={i} className="aspect-[4/3] overflow-hidden rounded-lg bg-white/5">
                            <img
                              src={url}
                              alt={`Photo ${i + 1}`}
                              className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-300 cursor-pointer"
                            />
                          </div>
                        )
                      )}
                      {/* placeholder tiles to simulate 30 */}
                      {Array.from({ length: 21 }).map((_, i) => (
                        <div key={`ph-${i}`} className="aspect-[4/3] rounded-lg bg-white/4 animate-pulse" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Floorplans / Map placeholders */}
            {(fullscreenTab === 'floorplans' || fullscreenTab === 'map') && (
              <div className="flex items-center justify-center h-full text-white/25 text-base">
                {fullscreenTab === 'floorplans' ? 'Floor plans' : 'Map view'} — coming soon
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
